// Design tokens extracted from dashboard (1).jsx prototype

export const colors = {
  background: '#FAFAF8',
  surface: '#FFFFFF',
  text: {
    primary: '#1A1A1A',
    secondary: '#555',
    tertiary: '#888',
    muted: '#999',
    disabled: '#AAA',
    faint: '#CCC',
  },
  border: {
    default: '#E5E5E0',
    light: '#F0F0EC',
  },
  tableHeader: '#F8F8F6',
  addOn: { bg: '#F3E5F5', color: '#6A1B9A', border: '#CE93D8' },
  // Item E (E1) — CRM Notes chip/accent. Teal, deliberately OUTSIDE the reserved field-state
  // families (green=AI proposal, blue=user-set, amber=warning, red=error) so a note never reads as a
  // proposal/edit. Design-owner approved 2026-07-10; changeable here without touching components.
  note: { color: '#1B7A8E', bg: '#E1F0F3', border: '#A9D2DC' },
  accent: {
    live: '#1A1A1A',
    screening: '#E65100',
    origination: '#1565C0',
    sectors: '#2E7D32',
    pending: '#E65100',
  },
  badge: {
    new: { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
    pendingUpdate: { bg: '#FFF8E1', color: '#B7791F', border: '#F5D88F' },
    newDeal: { bg: '#FFF3E0', color: '#E65100', border: '#FFB74D' },
    moved: { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
    // Backward stage transition (e.g. Live → Origination). Muted grey to
    // signal "slipped" without alarming.
    moved_back: { bg: '#F5F5F5', color: '#616161', border: '#C8C8C2' },
    // 'stale' badge deactivated 2026-06-17 (days-in-stage is the indicator).
    passed: { bg: '#F5F5F5', color: '#616161', border: '#C8C8C2' },
    lost:   { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
    hold:   { bg: '#FFF3E0', color: '#E65100', border: '#FFB74D' },
    // Deal closed (won) — Portfolio stage. Deep evergreen on soft-green;
    // same family as `moved` (forward progression) but distinguished by the
    // 🎉 emoji prefix in the label so reviewers don't misread it as a
    // mid-pipeline stage move.
    closed: { bg: '#E8F5E9', color: '#1F5132', border: '#A5D6A7' },
  },
  sectorStage: {
    active: { bg: '#E8F5E9', color: '#2E7D32' },
    monitoring: { bg: '#E3F2FD', color: '#1565C0' },
    emerging: { bg: '#F3E5F5', color: '#6A1B9A' },
    'on-hold': { bg: '#FFF3E0', color: '#E65100' },
    closed: { bg: '#F5F5F5', color: '#999' },
  },
  statusRecency: {
    green: '#2E7D32',
    orange: '#E65100',
    red: '#C62828',
  },
  // Intelligence-chunk quadrant accents (from chunk_viewer prototype).
  // Used on Presentation-mode sector slides to differentiate quadrants.
  quadrant: {
    market_trends:   '#5F5E5A',  // slate
    transactions:    '#BA7517',  // amber
    capital_markets: '#185FA5',  // blue
    news_regulatory: '#639922',  // green
  },
  // Sourcing classification accents — Metrics tab "Originated by classification"
  // stacked bar. Proprietary uses the firm's "win" green; auction the warning
  // orange we already use for screening/pending; limited process a neutral
  // teal between them; unclassified the soft grey.
  classification: {
    proprietary:     '#1F5132',  // deep evergreen
    limited_process: '#1B7A8E',  // teal
    auction:         '#E65100',  // orange (matches screening/pending accent)
    unclassified:    '#A8A8A2',  // muted grey
  },
  // Candidate (D8 proposed) deals on the Overview tab — the "Include AI-proposed deals" lens.
  // `proposed` is the extra stack segment on Pipeline by Sector and Pipeline Composition. It has to
  // separate from the sections it sits beside (live #1A1A1A, origination #1565C0, pending #999),
  // and in practice the stack is live + origination alone, so the blue is the pair that matters:
  // mulberry clears it (ΔE 22.6 normal / 11.6 protan, and 22.2 vs the grey).
  // `fresh` / `aging` / `stale` are the age bands on AI Proposed Deals by Person — the conventional
  // green/amber/red, re-stepped so green↔amber survives red-blindness (ΔE 9.0 protan; the stock
  // #B7791F amber collapses to 4.7) and every step holds 3:1 against the card surface.
  // All four validated with the dataviz skill's palette checker — re-run it if you change one.
  candidate: {
    proposed: '#A63C7B',  // mulberry
    fresh:    '#2E7D32',  // ≤ 1 week
    aging:    '#C2870B',  // 1–4 weeks
    stale:    '#C62828',  // > 4 weeks
  },
  origUnassigned: '#FFFBF8',
  highlight: {
    new:      { border: '#1565C0', bg: '#F5F9FF' },
    moved:    { border: '#2E7D32', bg: '#F5FBF6' },
    meetings: { border: '#E65100', bg: '#FFFBF8' },
    sector:   { border: '#2E7D32', bg: '#F5FBF6' },
  },
  // Admin → LLM Spend panel. `overCap` is the danger red used for the (leading)
  // Daily-cap column + over-cap Today cells + the editable global-ceiling cell
  // (matches the legacy inline #c0392b). `categorical` is the per-purpose
  // stacked-bar palette for LlmSpendChart — the dataviz skill's validated default
  // 8-hue order (light surface), which passes the adjacent-pair CVD/normal-vision
  // gates for stacked bars. Purposes beyond slot 8 fold into a grey "Other".
  // The panel's per-purpose table is the accessibility "relief" for the three
  // sub-3:1 slots (magenta/yellow/aqua). `other` is the overflow grey.
  llmSpend: {
    overCap: '#c0392b',
    categorical: [
      '#2a78d6', // blue
      '#008300', // green
      '#e87ba4', // magenta
      '#eda100', // yellow
      '#1baf7a', // aqua
      '#eb6834', // orange
      '#4a3aa7', // violet
      '#e34948', // red
    ],
    other: '#8a8a82', // grey — folded overflow (>8 purposes)
  },
} as const;

export const fonts = {
  sans: "'DM Sans', 'Helvetica Neue', sans-serif",
  mono: "'DM Mono', monospace",
} as const;

export const daysThresholds = {
  danger: 25,
  warning: 14,
} as const;

export const activityThresholds = {
  emailHigh: 5,
  meetingHigh: 2,
} as const;

